import { useState } from "react";

import { Element, useEditor, useNode } from "@craftjs/core";
import { PlusIcon, Rows2 } from "lucide-react";
import { cn } from "twyne-new.ui";

import { define, prop, variant } from "@/components/builder/utils";

import BasePreview, { Action } from "./base";

import { PlaceholderBlock } from ".";

function FlexPreview({ children, properties, properties: { flexRows }, ...props }) {
  const { id } = useNode()
  const { actions, query: { createNode } } = useEditor();

  const handleAddItem = () => {
    if (!children) return
    actions.add(createNode(<Element is={PlaceholderBlock} canvas />), id)
  }

  return <BasePreview properties={properties} {...props} customActions={<>
    <Action onClick={handleAddItem} className='rounded-full! p-1!'>
      <PlusIcon size={12} />
    </Action>
  </>}>
    <div>
      {children}
      {Array.from({ length: flexRows }).map((_, idx) => <Element id={`placeholder-${idx}`} is={PlaceholderBlock} key={idx} canvas />)}
    </div>
  </BasePreview>
}

const MAX_ROWS = 6

function VariantPreview({ onChangeProps, flex: { direction } }) {
  const [selected, setSelected] = useState(-1)
  const [hovered, setHovered] = useState(false)

  const handleChange = ridx => () => {
    setSelected(ridx)
    onChangeProps({
      flexRows: ridx + 1,
    })
  }

  return <div className="flex justify-center items-center w-full h-full">
    <div className={`flex ${direction} gap-1 flex-nowrap w-full`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {Array
        .from({ length: MAX_ROWS })
        .map((_, ridx) => {
          const isHovered = ridx <= selected && hovered

          return <div
            key={`${ridx}`}
            onMouseEnter={handleChange(ridx)}
            className={cn(`border-[2px] rounded-md p-6 transition-all duration-200 h-10`, {
              'border-gray-300 dark:border-gray-400': !isHovered,
              'bg-blue-400/20 dark:bg-blue-400/20': isHovered,
              'border-blue-400/20 dark:border-blue-400/20': isHovered,
            })}
          ></div>
        })}
    </div>
  </div>
}

function Label({ label, children }) {
  return <div className='w-full flex flex-col gap-2 items-center justify-center text-center'>
    <span className='bg-gray-100 dark:bg-gray-600 rounded-md p-2 text-sm text-gray-500 dark:text-gray-200 w-full'>{label}</span>
    {children}
  </div>
}

export default define('flex', FlexPreview)
  .canvas()
  .palete(<Rows2 size={16} />, 'Flex', 'Structures')
  .property(prop.new('Variant', 'variant').hide().own())
  .property(prop.predefined.customClass())
  .property(...prop.predefined.layout())
  .property(...prop.predefined.appearance())
  .property(...prop.predefined.container())
  .property(prop.predefined.attributes())
  .variant(variant(VariantPreview, null)
    .add('col', 'Col', {
      layoutType: 'flex',
      flex: { direction: 'flex-row', align: 'items-center', distribute: 'justify-center' },
    }, { LabelTag: Label })
    .add('row', 'Row', { flex: { direction: 'flex-col' } }, { LabelTag: Label }))
  .defaultProps({
    layoutType: 'flex',
    flex: { gap: '2' },
  })
  .ignoreResetProps(['variant', 'flexRows', 'layoutType', 'flex.direction', 'flex.align', 'flex.distribute'])
  .build()
